import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import Banner from '../common/Banner';
import { useToast } from '../common/Toast';
import { useAuth } from '../../context/authcontext';
import { SetupStatusService } from '../../services/admin/setupStatus.service';
import type { SetupItemKey, SetupStatus, SetupStatusItem } from '../../dtos/admin/setupStatus.dto';
import { isApiError } from '../../dtos/common/api.dto';
import { canManageAgencySetup } from '../../utils/baaAccess';
import { EVV_SETUP_UPDATED_EVENT, SETUP_STATUS_UPDATED_EVENT } from '../../constants/navigationEvents';

export const SETUP_ITEM_LABELS: Record<SetupItemKey, string> = {
  agencyProfile: 'Complete the agency profile',
  evvChecklist: 'Finish the EVV checklist',
  staffUser: 'Add a staff user',
  patient: 'Add a patient',
};

/** Shown while the EVV checklist is not done (the claim block itself is Phase 11). */
export const EVV_BLOCKED_COPY = 'Medicaid claims are blocked until EVV setup is complete.';

/** Event other screens fire after a setup change (agency save, EVV step, user/patient create). */
export const SETUP_UPDATED_EVENTS = [EVV_SETUP_UPDATED_EVENT, SETUP_STATUS_UPDATED_EVENT];

export interface SetupChecklistProps {
  /** Open the screen for an item (the dashboard maps keys to its admin tabs). */
  onNavigate?: (key: SetupItemKey, item: SetupStatusItem) => void;
  className?: string;
}

const ITEM_ORDER: SetupItemKey[] = ['agencyProfile', 'evvChecklist', 'staffUser', 'patient'];

const SetupChecklist: React.FC<SetupChecklistProps> = ({ onNavigate, className = '' }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  // Owners and admins only (roles live in the DB, so this reads permissions, not Cognito groups);
  // the endpoint returns 403 to everyone else and the checklist then renders nothing
  const isSetupRole = canManageAgencySetup(user);
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await SetupStatusService.getStatus());
    } catch {
      // A 403 (role changed) or a failed request hides the checklist; it is a hint, not a gate
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    if (!isSetupRole) {
      setStatus(null);
      return undefined;
    }
    load();
    const handler = () => { load(); };
    SETUP_UPDATED_EVENTS.forEach((name) => window.addEventListener(name, handler));
    return () => SETUP_UPDATED_EVENTS.forEach((name) => window.removeEventListener(name, handler));
  }, [isSetupRole, load]);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await SetupStatusService.dismiss();
      setStatus((prev) => (prev ? { ...prev, dismissedAt: new Date().toISOString() } : prev));
    } catch (err) {
      if (isApiError(err) && (err.statusCode === 409 || err.domainCode === 'SETUP_INCOMPLETE')) {
        showToast('Setup is not complete yet. Finish every item first.', { variant: 'warning' });
        load();
      } else {
        showToast("Couldn't dismiss the setup checklist. Please try again.", { variant: 'error' });
      }
    } finally {
      setDismissing(false);
    }
  };

  if (!isSetupRole || !status || status.dismissedAt) return null;

  const items = ITEM_ORDER
    .map((key) => status.items.find((item) => item.key === key))
    .filter((item): item is SetupStatusItem => item !== undefined);
  const evvItem = items.find((item) => item.key === 'evvChecklist');
  const evvIncomplete = evvItem ? !evvItem.done : false;

  const list = (
    <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="setup-checklist-items">
      {items.map((item) => (
        <li key={item.key} className="flex items-start gap-2" data-testid={`setup-item-${item.key}`}>
          {item.done ? (
            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-green-600" aria-label="Done" />
          ) : (
            <Circle size={16} className="mt-0.5 flex-shrink-0 opacity-60" aria-label="Not done" />
          )}
          <div className="min-w-0">
            {item.done || !onNavigate ? (
              <span className={item.done ? 'line-through opacity-70' : ''}>{SETUP_ITEM_LABELS[item.key]}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(item.key, item)}
                className="text-left font-medium underline hover:no-underline"
              >
                {SETUP_ITEM_LABELS[item.key]}
              </button>
            )}
            {!item.done && item.missing && item.missing.length > 0 && (
              <p className="text-xs opacity-80">Missing: {item.missing.join(', ')}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );

  if (status.allDone) {
    return (
      <Banner
        variant="info"
        title="Agency setup complete"
        className={className}
        data-testid="setup-checklist"
        action={(
          <button
            type="button"
            onClick={handleDismiss}
            disabled={dismissing}
            className="rounded-md border border-sky-300 bg-white px-3 py-1 text-sm font-medium text-sky-900 hover:bg-sky-100 disabled:opacity-50"
          >
            Dismiss
          </button>
        )}
      >
        {list}
      </Banner>
    );
  }

  return (
    <Banner variant="warning" title="Finish setting up your agency" className={className} data-testid="setup-checklist">
      {evvIncomplete && <p>{EVV_BLOCKED_COPY}</p>}
      {list}
    </Banner>
  );
};

export default SetupChecklist;
